import {Alert, Box, Button, Container, Stack} from "@mui/material";
import {Elements, useStripe} from "@stripe/react-stripe-js";
import {useNavigate} from "@tanstack/react-router";
import type * as React from "react";
import {useContext, useEffect, useState} from "react";
import {stripePromise} from "@/components/Forms/CartPaymentForm.tsx";
import {useCartContext} from "@/components/Providers/CartProvider.tsx";
import {useFeeProvider} from "@/components/Providers/FeesProvider.tsx";
import {jwtContext} from "@/components/Providers/JWTProvider.tsx";
import StandardPage from "@/components/StandardPage.tsx";
import useStripeCompleteSetupIntent from "@/hooks/useStripeCompleteSetupIntent";
import type {CreateSetupIntentResponse} from "@/types/CreateSetupIntentResponse.ts";

const PaymentComplete = (): React.ReactElement => {
    return (
        <Elements stripe={stripePromise}>
            <StandardPage>
                <Box
                    sx={{
                        flexGrow: 1,
                        display: "flex",
                        minHeight: "80vh",
                    }}
                >
                    <Box
                        sx={{
                            flexGrow: 1,
                            justifyContent: "center",
                            width: "100%",
                            backgroundColor: "#fff",
                            px: 2,
                            pt: 2,
                            borderRadius: 1,
                        }}
                    >
                        <FinishPayment />
                    </Box>
                </Box>
            </StandardPage>
        </Elements>
    );
};

const FinishPayment = () => {
    const stripe = useStripe();
    const navigate = useNavigate();
    const user = useContext(jwtContext);
    const {refreshCart} = useCartContext();
    const {refreshPurchaseHistory} = useFeeProvider();
    const completeSetupIntent = useStripeCompleteSetupIntent();
    const [paymentProcessed, setPaymentProcessed] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [secondsUntilRedirect, setSecondsUntilRedirect] = useState<number | null>(null);
    const searchParams = new URLSearchParams(window.location.search);

    useEffect(() => {
        const doCompletePayment = async () => {
            const cartId = searchParams.get("cartId");
            const courseSectionId = searchParams.get("courseSectionId");
            const payNowAmount = Number.parseFloat(searchParams.get("payNowAmount") ?? "1");

            if (
                user !== null &&
                user !== undefined &&
                (cartId !== null || courseSectionId !== null) &&
                payNowAmount !== null &&
                stripe
            ) {
                setPaymentProcessed(true);

                const setupIntentId = searchParams.get("setup_intent");
                const setupIntentClientSecret = searchParams.get("setup_intent_client_secret");

                const paymentIntentId = searchParams.get("payment_intent");
                const paymentIntentClientSecret = searchParams.get("payment_intent_client_secret");

                let paymentMethod: string | undefined;

                if (paymentIntentId && paymentIntentClientSecret) {
                    const paymentIntent =
                        await stripe.retrievePaymentIntent(paymentIntentClientSecret);
                    if (paymentIntent.error) {
                        setErrorMessage(
                            paymentIntent.error.message ?? "An unknown payment error has occurred.",
                        );
                        return;
                    }
                    if (typeof paymentIntent.paymentIntent.payment_method === "string") {
                        paymentMethod = paymentIntent.paymentIntent.payment_method;
                    } else if (paymentIntent.paymentIntent.payment_method) {
                        paymentMethod = paymentIntent.paymentIntent.payment_method.id;
                    }
                }
                if (setupIntentId && setupIntentClientSecret) {
                    const retrieveSetupIntentResponse =
                        await stripe.retrieveSetupIntent(setupIntentClientSecret);

                    if (retrieveSetupIntentResponse.error) {
                        setErrorMessage(
                            retrieveSetupIntentResponse.error.message ??
                                "An unknown payment error has occurred.",
                        );
                        return;
                    }
                    if (
                        typeof retrieveSetupIntentResponse.setupIntent.payment_method === "string"
                    ) {
                        paymentMethod = retrieveSetupIntentResponse.setupIntent.payment_method;
                    } else if (retrieveSetupIntentResponse.setupIntent.payment_method) {
                        paymentMethod = retrieveSetupIntentResponse.setupIntent.payment_method.id;
                    }
                }

                if (!paymentMethod) {
                    setErrorMessage("There was an error getting your payment method.");
                    return;
                }

                const response = await completeSetupIntent(
                    user,
                    setupIntentId,
                    paymentMethod,
                    cartId,
                    courseSectionId,
                    payNowAmount,
                    paymentIntentId,
                );

                if (response.ok) {
                    const json = (await response.json()) as CreateSetupIntentResponse;

                    if (json.errorCode !== 0) {
                        setErrorMessage(json.message);
                        return;
                    }

                    await refreshCart();
                    await refreshPurchaseHistory();
                    setSuccessMessage("Payment Complete");
                    setSecondsUntilRedirect(5);
                } else {
                    setErrorMessage("An unknown error has occurred.");
                }
            }
        };

        if (!paymentProcessed) {
            void doCompletePayment();
        }
    }, [user, stripe]);

    useEffect(() => {
        if (secondsUntilRedirect !== null) {
            window.setTimeout(() => {
                if (secondsUntilRedirect > 0) {
                    setSecondsUntilRedirect(secondsUntilRedirect - 1);
                    return;
                }

                setSecondsUntilRedirect(null);
                navigate({to: "/programs"});
            }, 1000);
        }
    }, [secondsUntilRedirect]);

    return (
        <Stack direction="column" alignItems="center">
            <Container>
                {errorMessage && (
                    <Stack direction="column" alignItems="center">
                        <Stack direction="row" justifyContent="center" sx={{mb: 2}}>
                            There has been an error processing your request.
                        </Stack>
                        <Alert severity="error">{errorMessage}</Alert>
                        <Button
                            variant="outlined"
                            onClick={async () => {
                                await navigate({to: "/programs"});
                            }}
                            sx={{mt: 2}}
                        >
                            Click here to return to Registration
                        </Button>
                    </Stack>
                )}
                {successMessage && (
                    <Stack direction="column" alignItems="center">
                        <Stack direction="row" justifyContent="center" sx={{mb: 2}}>
                            You will be redirected to Registration in {secondsUntilRedirect}...
                        </Stack>
                        <Alert severity="success">{successMessage}</Alert>
                        <Button
                            variant="outlined"
                            onClick={async () => {
                                await navigate({to: "/programs"});
                            }}
                            sx={{mt: 2}}
                        >
                            Return to Registration Now
                        </Button>
                    </Stack>
                )}
            </Container>
        </Stack>
    );
};

export default PaymentComplete;
